import React, { useEffect, useState } from "react";
import { Card, Tab, Nav, Modal, Spinner } from "react-bootstrap";
import { Link, useLocation, useParams } from 'react-router-dom';
import { InfoCircle } from "iconsax-react";
import DatiAnagraficiJunior from "../../../../components/Junior/datiAnagrafici_junior";
import SportJunior from "../../../../components/Junior/Sport_junior";
import TraguardiJunior from "../../../../components/Junior/traguardi_junior";
import TesseramentiPlayer from "../../../../components/Clienti/tesseramenti_player";
import DatiSanPlayer from "../../../../components/Clienti/datiSanitari_player";
import { tipologiaStruttura } from "../../../../components/Util/enum";
import MyApiClient from "../../../../../api-client/MyApiClient";
import DynamicLogo from "../../../DynamicLogoLoader";
import { usePageTitle, useStatoInSolaLettura } from "../../../../layouts/PageHeaderExtra";

const STATUS_MAP = {
	ACTIVE: ['Attivo', 'success'],
	COMPLETED: ['Completato', 'success'],
	INACTIVE: ['Inattivo', 'danger'],
	SUSPENDED: ['Sospeso', 'warning'],
};

export const JuniorProfile = () => {

	const { id } = useParams();
	const location = useLocation();

	const [loadingShowed, setLoadingShowed] = useState(false);
	const [profile, setProfile] = useState(null);
	// Il profilo personale e` un'altra chiamata: i dati sanitari e il certificato
	// medico stanno li`, non nel profilo pubblico.
	const [privateProfile, setPrivateProfile] = useState(null);
	const [labelNome, setLabelNome] = useState('');
	// Titolo della pagina = nome del junior; finché non è caricato resta quello generico
	usePageTitle(labelNome);

	// Le tessere le rilasciano solo ASD e SSD: stessa regola della scheda del player.
	let clubNature = null;
	const tokenDetailsString = sessionStorage.getItem('userDetails');
	if (tokenDetailsString) {
		clubNature = JSON.parse(tokenDetailsString).clubNature;
	}
	const showTesserati =
		clubNature === tipologiaStruttura.ASD.value || clubNature === tipologiaStruttura.SSD.value;

	const tabData = [
		{ id: 'anagrafica', name: "Anagrafica", content: <DatiAnagraficiJunior profile={profile} /> },
		{ id: 'sport', name: "Sport", content: <SportJunior profile={profile} /> },
		// I campi sanitari del junior hanno la stessa forma di quelli del player, e il
		// certificato medico lo carica il genitore dall'app: si riusa lo stesso componente.
		{ id: 'datisanitari', name: "Dati sanitari", content: <DatiSanPlayer privateProfile={privateProfile} /> },
		{ id: 'traguardi', name: "Traguardi", content: <TraguardiJunior profile={profile} /> },
		...(showTesserati ? [{
			id: 'tesseramenti',
			name: "Tesseramenti",
			// Le tessere di un junior non stanno nel suo profilo: il componente le chiede
			// all'endpoint per userId, lo stesso che usa la scheda del player.
			content: <TesseramentiPlayer profile={profile} userId={id} />,
		}] : []),
	];

	const query = new URLSearchParams(location.search);
	const [defaultTab, setDefaultTab] = useState(query.get('tab') || tabData[0].id);

	useEffect(() => {
		const tab = new URLSearchParams(location.search).get('tab');
		if (tab && tab !== defaultTab) setDefaultTab(tab);
	}, [location.search, defaultTab]);

	useEffect(() => {
		const fetchData = async () => {
			try {
				const tokenDetailsString = sessionStorage.getItem('userDetails');
				if (tokenDetailsString) {
					const token = JSON.parse(tokenDetailsString).idToken;
					const apiClient = new MyApiClient();

					setLoadingShowed(true);
					// Il profilo personale e` un di piu`: se non arriva, la scheda deve
					// aprirsi lo stesso con anagrafica, sport e traguardi. Senza questo
					// `catch` un suo errore faceva fallire l'intera Promise.all, e la
					// pagina restava sullo spinner per sempre.
					const [profileData, privateProfileData] = await Promise.all([
						apiClient.getProfiles(token, id),
						apiClient.getPrivateProfile(token, id).catch(() => null)
					]);

					if (profileData.junior) {
						setLabelNome(`${profileData.junior.givenName} ${profileData.junior.familyName}`);
					}
					setProfile(profileData);
					setPrivateProfile(privateProfileData);
				}
			} catch (error) {
				console.error("Error fetching data:", error);
			} finally {
				// Lo spinner si chiude comunque sia andata: e` una finestra modale, e
				// lasciarla aperta blocca la pagina.
				setLoadingShowed(false);
			}
		};
		fetchData();
	}, [id]);

	const j = profile && profile.junior;
	const initials = j ? `${(j.givenName || '')[0] || ''}${(j.familyName || '')[0] || ''}` : '';
	const [statusLabel, statusVariant] = (j && STATUS_MAP[j.profileStatus]) || ['', 'muted'];
	// Stato del profilo in sola lettura subito dopo il titolo (manca l'API per cambiarlo)
	useStatoInSolaLettura(statusLabel, statusVariant === 'success' ? 'attivo' : ['danger', 'warning'].includes(statusVariant) ? 'non-attivo' : 'bozza');
	const parent = j && j.parent;
	const parentName = parent ? `${parent.givenName || ''} ${parent.familyName || ''}`.trim() : '';

	return (
		<>
			<Modal className="fade" show={loadingShowed} centered>
				<Modal.Body className="text-center">
					<Spinner animation="border" role="status">
						<span className="sr-only">Loading...</span>
					</Spinner>
					<div>Caricamento in corso...</div>
				</Modal.Body>
			</Modal>

			{/* Header profilo */}
			<Card className="mb-4">
				<Card.Body className="d-flex justify-content-between align-items-start flex-wrap gap-3">
					<div className="d-flex align-items-center gap-3">
						<div className="sid-avatar-lg">
							{j && j.picture ? <DynamicLogo logoUrl={j.picture} /> : initials}
						</div>
						<div>
							<h4 className="mb-1">{labelNome}</h4>
							<div className="d-flex align-items-center gap-3 flex-wrap">
								{parent && (
									<span className="text-muted">
										Senior: <Link to={`/struttura/clienti/player_profile/${parent.userId}`} style={{ color: '#E8721C', fontWeight: 600 }}>{parentName}</Link>
									</span>
								)}
							</div>
						</div>
					</div>
					<div className="sid-readonly-note">
						<InfoCircle size={20} variant="Bulk" />
						<span><strong>Dati in sola lettura.</strong> Modificabili solo dal Player tramite l'app Sportid Player.</span>
					</div>
				</Card.Body>
			</Card>

			{/* Tabs */}
			<div className="custom-tab-1">
				<Tab.Container defaultActiveKey={defaultTab}>
					<Nav as="ul" className="nav-tabs" style={{ border: 'none' }}>
						{tabData.map((data, i) => (
							<Nav.Item as="li" key={i} className="tabMenu">
								<Nav.Link eventKey={data.id}>{data.name}</Nav.Link>
							</Nav.Item>
						))}
					</Nav>
					<Tab.Content className="mt-4">
						{tabData.map((data, i) => (
							<Tab.Pane eventKey={data.id} key={i}>
								{data.content}
							</Tab.Pane>
						))}
					</Tab.Content>
				</Tab.Container>
			</div>
		</>
	);
}

export default JuniorProfile;
